import PropTypes from "prop-types";
import { useMemo } from "react";

// material-ui
import { CssBaseline, StyledEngineProvider } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

// project import
import { TIENDA_HANDY } from "config";
import { useBranding } from "contexts/BrandingContext";
import useConfig from "hooks/useConfig";
import Palette from "./palette";
import Typography from "./typography";

import componentStyleOverrides from "./compStyleOverride";
import customShadows from "./shadows";

export default function ThemeCustomization({ children }) {
  const { borderRadius, fontFamily, navType, outlinedFilled, presetColor, rtlLayout } = useConfig();
  const { branding } = useBranding();

  const theme = useMemo(() => Palette(navType, presetColor), [navType, presetColor]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const themeTypography = useMemo(() => Typography(theme, borderRadius, fontFamily), [theme, borderRadius, fontFamily]);
  const themeCustomShadows = useMemo(() => customShadows(navType, theme), [navType, theme]);

  // Crear override de colores dinámicos basados en branding
  const dynamicPalette = useMemo(() => {
    const basePalette = theme.palette;

    // Manejar estructura {data: {...}} que viene del API
    const brandingData = branding?.data || branding;

    // Si hay branding, override los colores
    if (brandingData?.branding) {
      const overrides = {};

      if (brandingData.branding.primary_color) {
        overrides.primary = {
          ...basePalette.primary,
          main: brandingData.branding.primary_color,
          light: brandingData.branding.primary_color + "80",
          dark: brandingData.branding.primary_color,
        };
      }

      if (brandingData.branding.secondary_color) {
        overrides.secondary = {
          ...basePalette.secondary,
          main: brandingData.branding.secondary_color,
          light: brandingData.branding.secondary_color + "80",
          dark: brandingData.branding.secondary_color,
        };
      }
      console.log("🎨 Applying dynamic palette overrides:", overrides);

      return {
        ...basePalette,
        ...overrides,
      };
    }

    return basePalette;
  }, [theme.palette, branding]);

  const themeOptions = useMemo(
    () => ({
      direction: rtlLayout ? "rtl" : "ltr",
      palette: dynamicPalette, // Usar la paleta dinámica en lugar de theme.palette
      mixins: {
        toolbar: {
          minHeight: "48px",
          padding: "16px",
          "@media (min-width: 400px)": {
            minHeight: "48px",
          },
        },
      },
      typography: {
        ...themeTypography,
        fontSize: 16, // Aumentar el tamaño de fuente base de 14px a 16px
        htmlFontSize: 16, // Aumentar el tamaño de fuente HTML base
      },
      customShadows: themeCustomShadows,
    }),
    [rtlLayout, dynamicPalette, themeCustomShadows, themeTypography]
  );

  const themes = createTheme(themeOptions);
  themes.components = useMemo(() => componentStyleOverrides(themes, borderRadius, outlinedFilled), [themes, borderRadius, outlinedFilled]);

  // Log para debugging
  console.log("🎨 Theme created with palette:", themes.palette.primary, themes.palette.secondary);

  // CSS Global para borderRadius con TIENDA_HANDY
  const globalStyles = useMemo(
    () => ({
      "@global": {
        // Si es TIENDA_HANDY, aplicar border-radius: 0 !important a elementos específicos
        ...(TIENDA_HANDY
          ? {
              '* [style*="border-radius"]': {
                borderRadius: "0 !important",
              },
              ".MuiCard-root, .MuiPaper-root, .MuiButton-root, .MuiChip-root, .MuiDialog-paper, .MuiMenu-paper, .MuiPopover-paper, .MuiAlert-root": {
                borderRadius: "0 !important",
              },
            }
          : {}),
      },
    }),
    []
  );

  // Agregar estilos globales al tema
  themes.components = {
    ...themes.components,
    MuiCssBaseline: {
      styleOverrides: globalStyles,
    },
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

ThemeCustomization.propTypes = {
  children: PropTypes.node,
};
